// Replay is a PURELY CLIENT-SIDE, READ-ONLY re-presentation of a completed
// round. It NEVER touches the network, the wallet, the state machine, or the
// idempotency keys: it only reconstructs the visual inputs (dice + match flags)
// from already-stored authoritative history and hands them to the same animator
// the live flow uses. There is no `roll()` here and no new round can be created.
import type { PublicReplay } from '@aerofruits/game-sdk';
import type { HistoryEntry } from './controller.js';
import type { LightningTier } from './lightning-types.js';
import { resolveMultiplierTenths } from './lightning-types.js';

export interface ReplayPlan {
  roundId: string;
  /** The number the player had picked for this historical round. */
  selectedNumber: number;
  /** The exact server dice for this round (never regenerated). */
  dice: number[];
  /** Which dice matched the pick — derived from the stored dice + pick. */
  matchFlags: boolean[];
  matchCount: number;
  multiplier: number;
  betMinor: number;
  winMinor: number;
  currency: string;
  timestampMs: number;
  lightningTier?: LightningTier;
  baseMultiplierTenths?: number;
  lightningMultiplierTenths?: number;
  totalMultiplierTenths?: number;
}

/**
 * Builds the visual replay inputs for a completed round from stored history.
 * Returns null when the entry lacks the dice needed to reconstruct a roll, so
 * callers can safely skip un-replayable rows instead of faking data.
 */
export function planReplay(entry: HistoryEntry): ReplayPlan | null {
  if (!Array.isArray(entry.dice) || entry.dice.length === 0) return null;
  const dice = entry.dice.map((d) => (Number.isInteger(d) && d >= 1 && d <= 6 ? d : 1));
  const selectedNumber = entry.selectedNumber;
  const matchFlags = dice.map((d) => d === selectedNumber);
  // Prefer the stored authoritative count; fall back to the derived flags so a
  // replay still highlights correctly even if the count was not persisted.
  const derived = matchFlags.filter(Boolean).length;
  const matchCount = Number.isInteger(entry.matchCount) ? entry.matchCount : derived;
  const plan: ReplayPlan = {
    roundId: entry.roundId,
    selectedNumber,
    dice,
    matchFlags,
    matchCount,
    multiplier: resolveMultiplierTenths(entry.multiplier ?? 0, {
      totalMultiplierTenths: entry.totalMultiplierTenths,
      lightning: Boolean(entry.lightningTier),
    }),
    betMinor: entry.betMinor,
    winMinor: entry.winMinor,
    currency: entry.currency,
    timestampMs: entry.timestampMs,
  };
  if (entry.lightningTier) {
    plan.lightningTier = entry.lightningTier as LightningTier;
    if (entry.baseMultiplierTenths !== undefined) plan.baseMultiplierTenths = entry.baseMultiplierTenths;
    if (entry.lightningMultiplierTenths !== undefined) {
      plan.lightningMultiplierTenths = entry.lightningMultiplierTenths;
    }
    if (entry.totalMultiplierTenths !== undefined) plan.totalMultiplierTenths = entry.totalMultiplierTenths;
  }
  return plan;
}

/**
 * Builds a replay plan from a PUBLIC replay projection fetched by id. Used by
 * the standalone replay page, which has authoritative stored data but no
 * in-session history. Returns null when the stored dice are missing/invalid so
 * the page can show an honest "unavailable" state instead of faking a result.
 */
export function planReplayFromPublic(r: PublicReplay): ReplayPlan | null {
  if (!Array.isArray(r.dice) || r.dice.length === 0) return null;
  const dice = r.dice.map((d) => (Number.isInteger(d) && d >= 1 && d <= 6 ? d : 1));
  const selectedNumber = r.selected_number;
  const matchFlags = dice.map((d) => d === selectedNumber);
  const derived = matchFlags.filter(Boolean).length;
  const matchCount = Number.isInteger(r.match_count) ? r.match_count : derived;
  const plan: ReplayPlan = {
    roundId: r.replay_id,
    selectedNumber,
    dice,
    matchFlags,
    matchCount,
    multiplier: resolveMultiplierTenths(r.multiplier ?? 0, {
      totalMultiplierTenths: r.total_multiplier_tenths,
      lightning: Boolean(r.lightning_tier),
    }),
    betMinor: r.bet_minor,
    winMinor: r.win_minor,
    currency: r.currency,
    timestampMs: r.created_at_unix > 0 ? r.created_at_unix * 1000 : Date.now(),
  };
  if (r.lightning_tier) {
    plan.lightningTier = r.lightning_tier as LightningTier;
    if (r.base_multiplier_tenths !== undefined) plan.baseMultiplierTenths = r.base_multiplier_tenths;
    if (r.lightning_multiplier_tenths !== undefined) {
      plan.lightningMultiplierTenths = r.lightning_multiplier_tenths;
    }
    if (r.total_multiplier_tenths !== undefined) plan.totalMultiplierTenths = r.total_multiplier_tenths;
  }
  return plan;
}
